import { useMemo, useState, useEffect } from "react";
import {
  _getTransferConfig,
  _calculateTransferFee,
} from "../common/axios_services";
import { showErrorNotification } from "../common/utils/notification";

const useTransferValidation = (userTier, navigate) => {
  const [apiConfig, setApiConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransferConfig();
  }, []);

  const fetchTransferConfig = async () => {
    try {
      setLoading(true);
      const response = await _getTransferConfig();
      setApiConfig(response.data);
    } catch (error) {
      handleApiError(error, navigate);
      setApiConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const transferConfig = useMemo(() => {
    if (!apiConfig) {
      return null;
    }

    const rawData = apiConfig?.data || apiConfig;
    const bank = rawData?.bank;
    const k2k = rawData?.k2k;

    const isTier2OrAbove =
      userTier === "tier_2" ||
      userTier === "2" ||
      userTier === "tier_3" ||
      userTier === "3";

    const bankMin = isTier2OrAbove
      ? (bank?.tier2MinAmount ?? bank?.minAmount ?? 1000)
      : (bank?.tier1MinAmount ?? bank?.minAmount ?? 1000);

    return {
      bankTransfer: {
        minAmount: Number(bankMin),
        tier1MinAmount: Number(bank?.tier1MinAmount ?? bank?.minAmount ?? 1000),
        tier2MinAmount: Number(bank?.tier2MinAmount ?? 1000),
        feePercentage: isTier2OrAbove ? (bank?.tier2Fee ?? 0) : (bank?.tier1Fee ?? 0),
        feeType: "percentage",
      },
      konnectTransfer: {
        minAmount: Number(k2k?.minAmount ?? 100),
        feePercentage: k2k?.tier2Fee ?? 0,
        feeType: "percentage",
      },
    };
  }, [apiConfig, userTier]);

  const validateTransferAmount = (amount, type) => {
    if (!transferConfig) return { valid: true };
    const config = transferConfig[type];
    if (!config) return { valid: false, error: "Invalid transfer type" };

    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      return { valid: false, error: "Please enter a valid transfer amount" };
    }

    if (config.minAmount && numAmount < config.minAmount) {
      return {
        valid: false,
        error: `Minimum ${type === "bankTransfer" ? "bank" : "transfer"} amount is ₦${Number(config.minAmount).toLocaleString()}`,
      };
    }

    return { valid: true };
  };

  const calculateTransferFees = async (amount, type) => {
    try {
      const transferType =
        type === "bankTransfer" ? "bank_transfer" : "konnect_transfer";
      const response = await _calculateTransferFee({
        amount,
        type: transferType,
      });

      return {
        fee: response.data?.data?.fee,
        total: response.data?.data?.total,
        feePercentage: response.data?.data?.feePercentage,
      };
    } catch (error) {
      return { error: "Failed to calculate fees" };
    }
  };

  const calculateTransferFeesSync = (amount, type) => {
    const config = transferConfig[type];
    if (!config) return { fee: 0, total: amount, feePercentage: 0 };

    const fee =
      config.feeType === "percentage"
        ? Math.round(amount * config.feePercentage)
        : config.feePercentage;

    return {
      fee,
      total: amount + fee,
      feePercentage: config.feePercentage,
    };
  };

  const getTierBenefits = () => {
    const isTier2 =
      userTier === "tier_2" ||
      userTier === "2" ||
      userTier === "tier_3" ||
      userTier === "3";

    return {
      isTier2,
      benefits: isTier2
        ? [
            "Zero bank transfer fees",
            "NUBAN account available",
            "Higher daily/monthly limits",
            "Priority support",
          ]
        : [
            "Upgrade to Tier 2 for zero fees",
            "Get NUBAN account",
            "Enhanced benefits",
          ],
    };
  };

  return {
    loading,
    transferConfig,
    apiConfig,
    validateTransferAmount,
    calculateTransferFees,
    calculateTransferFeesSync,
    getTierBenefits,
  };
};

const handleApiError = (err, navigate) => {
  showErrorNotification(
    err?.response?.data?.message || err?.message || "An error occurred",
  );
};

export default useTransferValidation;
